// HellosignAgreement.jsx
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { connect } from 'react-redux';
import { Grid, Typography, withStyles } from 'material-ui';
import TextField from 'material-ui/TextField';
import Button from 'material-ui/Button';
import CircularProgress from 'material-ui/Progress/CircularProgress';
import { RESTAPI_URI, RUNTIME_ENV } from '../../../../../config';

/* -------------------------------------------------------------------------- */
/*                                    Styles                                  */
/* -------------------------------------------------------------------------- */

const styles = () => ({
  container: {
    margin: '0 auto',
    paddingTop: 24,
    paddingBottom: 32,
  },

  pageTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 14,
  },

  section: {
    marginTop: 14,
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: 'rgba(0,0,0,0.72)',
  },

  card: {
    padding: 16,
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: 10,
    marginTop: 10,
    background: '#fff',
  },

  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },

  fieldWrap: {
    marginTop: 12,
  },

  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },

  twoColSingle: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
  },

  divider: {
    height: 1,
    background: 'rgba(0,0,0,0.08)',
    marginTop: 14,
    marginBottom: 14,
  },

  errorText: {
    color: '#b00020',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.5,
  },

  helperText: {
    fontSize: 12,
    color: 'rgba(0,0,0,0.6)',
    lineHeight: 1.5,
  },

  rolePill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 999,
    border: '1px solid rgba(0,0,0,0.15)',
    fontSize: 12,
    fontWeight: 600,
    background: 'rgba(0,0,0,0.03)',
  },

  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 999,
    border: '1px solid rgba(0,0,0,0.15)',
    fontSize: 12,
    fontWeight: 600,
    background: 'rgba(0,0,0,0.03)',
    whiteSpace: 'nowrap',
  },

  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    display: 'inline-block',
    marginRight: 8,
  },

  successText: {
    color: '#1b5e20',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.5,
  },

  bottomBar: {
    marginTop: 18,
    padding: 16,
    borderRadius: 12,
    border: '1px solid rgba(0,0,0,0.12)',
    background: 'rgba(0,0,0,0.02)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  bottomLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },

  bottomRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },

  primaryActionButton: {
    backgroundColor: '#1565c0',
    color: '#fff',
    fontWeight: 700,
    borderRadius: 10,
    padding: '10px 16px',
    textTransform: 'none',
    boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
  },

  secondaryButton: {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 600,
  },

  warningCard: {
    padding: 14,
    border: '1px solid rgba(176,0,32,0.25)',
    borderRadius: 10,
    marginTop: 10,
    background: 'rgba(176,0,32,0.04)',
  },

  warningTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: '#b00020',
    marginBottom: 6,
  },

  warningText: {
    fontSize: 12,
    color: 'rgba(0,0,0,0.72)',
    lineHeight: 1.5,
  },
});

/* -------------------------------------------------------------------------- */
/*                              Field Renderer                                */
/* -------------------------------------------------------------------------- */

const renderTextInput = ({ input, label, meta, type, disabled, prefillValue }) => {
  const hasReduxValue =
    input &&
    typeof input.value !== 'undefined' &&
    input.value !== null &&
    String(input.value).length > 0;

  const value = hasReduxValue
    ? input.value
    : prefillValue === null || typeof prefillValue === 'undefined'
      ? ''
      : String(prefillValue);

  return (
    <TextField
      {...input}
      value={value}
      fullWidth
      label={label}
      type={type || 'text'}
      disabled={!!disabled}
      error={meta.touched && !!meta.error}
      helperText={meta.touched && meta.error}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
    />
  );
};

renderTextInput.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  prefillValue: PropTypes.any,
};

renderTextInput.defaultProps = {
  type: 'text',
  disabled: false,
  prefillValue: '',
};

/* -------------------------------------------------------------------------- */
/*                                 Component                                  */
/* -------------------------------------------------------------------------- */

class HellosignAgreement extends Component {
  pollTimer = null;
  pollInFlight = false;
  _isMounted = false;

  state = {
    loading: false,
    error: null,

    offerId: null,
    templateId: null,
    template: null,

    actionLoading: false,
    actionError: null,
    actionMessage: null,

    signatureStatus: 'none', // none | pending | completed
    signatureRequestId: null,
    detailsUrl: null,

    checkingStatus: false,
    statusError: null,

    initialStatusLoading: true,
    initialStatusError: null,

    // Hide signer/custom fields until user clicks "Show details"
    showDetails: false,
  };

  componentDidMount() {
    this._isMounted = true;
    this.bootstrap();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.stopPolling();
  }

  componentDidUpdate(prevProps) {
    const prevOfferId = this.getOfferId(prevProps);
    const nextOfferId = this.getOfferId(this.props);

    if (prevOfferId !== nextOfferId) {
      this.stopPolling();
      this.bootstrap();
    }
  }

  /* ------------------------------ Form Sync ------------------------------ */

  syncStatusToForm = (signatureStatus) => {
    const { change } = this.props;
    if (typeof change === 'function') {
      change('hellosign_signature_status', signatureStatus);
    }
  };

  /* ------------------------------ UI Helpers ------------------------------ */

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  /* ------------------------------- Polling -------------------------------- */

  startPolling = (statusOverride) => {
    this.stopPolling();

    const status = statusOverride || this.state.signatureStatus;
    if (status !== 'pending') return;

    // Poll immediately once, then interval
    this.pollSignatureStatus();

    this.pollTimer = setInterval(() => {
      this.pollSignatureStatus();
    }, 8000);
  };

  stopPolling = () => {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  };

  pollSignatureStatus = async () => {
    const { offerId, signatureStatus } = this.state;

    if (!offerId || signatureStatus !== 'pending') return;
    if (this.pollInFlight) return;

    this.pollInFlight = true;

    try {
      await this.fetchAndApplyStatusByOffer(offerId, { silent: true });
    } finally {
      this.pollInFlight = false;
    }
  };

  /* ------------------------------- Helpers -------------------------------- */

  getOfferId = (props) => {
    const fromMatch =
      props &&
      props.match &&
      props.match.params &&
      (props.match.params.offerId || props.match.params.id);

    if (fromMatch) return String(fromMatch);

    try {
      const path = (window && window.location && window.location.pathname) || '';
      const parts = path.split('/').filter(Boolean);
      const last = parts.length ? parts[parts.length - 1] : null;
      if (last && last !== 'offer') return String(last);
    } catch (e) {
      // ignore
    }

    return null;
  };

  normalizeStatus = (status) => {
    const s = status ? String(status).toLowerCase() : '';
    if (s === 'completed' || s === 'signed') return 'completed';
    if (s === 'pending') return 'pending';
    return 'none';
  };

  getStatusDotStyle = () => {
    const { signatureStatus } = this.state;
    if (signatureStatus === 'completed') return { background: '#2e7d32' };
    if (signatureStatus === 'pending') return { background: '#ef6c00' };
    return { background: 'rgba(0,0,0,0.35)' };
  };

  getStatusLabel = () => {
    const { signatureStatus } = this.state;
    if (signatureStatus === 'completed') return 'Signed';
    if (signatureStatus === 'pending') return 'Pending signature';
    return 'Not sent';
  };

  /* ------------------------------ Error Helpers --------------------------- */

  // Returns a human-readable error message from various backend/HelloSign shapes
  // without ever rendering "[object Object]".
  getErrorMessage = async (res, json, fallback) => {
    // Prefer API-provided fields
    if (json) {
      if (typeof json === 'string') return json;

      if (json.error_msg) return String(json.error_msg);
      if (json.error_name) {
        return String(json.error_name);
      }

      if (typeof json.error === 'string') return json.error;
      if (typeof json.message === 'string') return json.message;

      if (json.error && typeof json.error === 'object') {
        if (json.error.error_msg) return String(json.error.error_msg);
        if (json.error.message) return String(json.error.message);
      }

      if (json.message && typeof json.message === 'object') {
        if (json.message.error_msg) return String(json.message.error_msg);
        if (json.message.message) return String(json.message.message);
      }
    }

    // If server returned text
    try {
      if (res && typeof res.text === 'function') {
        const text = await res.text();
        if (text) return text;
      }
    } catch (e) {
      // ignore
    }

    return fallback;
  };

  /* ------------------------------ Status API ------------------------------ */

  fetchAndApplyStatusByOffer = async (offerId, opts = {}) => {
    const { silent } = opts;

    if (!silent) {
      this.setState({ checkingStatus: true, statusError: null });
    }

    const url = `${RESTAPI_URI}/hellosign/signature-request/status/by-offer/${offerId}`;

    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: localStorage.getItem('authToken'),
        },
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json || !json.ok) {
        const msg = await this.getErrorMessage(
          res,
          json,
          `Failed to fetch status (${res ? res.status : 'unknown'})`
        );

        if (!silent) {
          this.setState({ statusError: msg });
        }
        return { ok: false, error: msg };
      }

      const normalized = this.normalizeStatus(json.status);
      const signatureRequestId = json.signatureRequestId ? String(json.signatureRequestId) : null;
      const detailsUrl = json.detailsUrl ? String(json.detailsUrl) : null;

      this.setState({
        signatureStatus: normalized,
        signatureRequestId,
        detailsUrl,
      });

      // Sync to redux-form so parent can block Next
      this.syncStatusToForm(normalized);

      // Start polling immediately when pending
      if (normalized === 'pending') this.startPolling('pending');
      else this.stopPolling();

      return { ok: true, status: normalized };
    } catch (e) {
      const msg = (e && e.message) || 'Failed to fetch status';
      if (!silent) {
        this.setState({ statusError: msg });
      }
      return { ok: false, error: msg };
    } finally {
      if (!silent) {
        this.setState({ checkingStatus: false });
      }
    }
  };

  /* ------------------------------- Bootstrap ------------------------------- */

  bootstrap = async () => {
    const offerId = this.getOfferId(this.props);

    // Reset UI for new offer
    this.setState({
      loading: false,
      error: null,

      offerId: offerId || null,
      templateId: null,
      template: null,

      actionLoading: false,
      actionError: null,
      actionMessage: null,

      signatureStatus: 'none',
      signatureRequestId: null,
      detailsUrl: null,

      checkingStatus: false,
      statusError: null,

      initialStatusLoading: true,
      initialStatusError: null,

      showDetails: false,
    });

    // Sync default immediately (parent can block until status fetch completes)
    this.syncStatusToForm('none');

    if (!offerId) {
      this.setState({
        initialStatusLoading: false,
        initialStatusError: 'Missing offerId. Expected URL like /offer/:offerId',
        error: 'Missing offerId. Expected URL like /offer/:offerId',
      });
      return;
    }

    // 1) Status-first: do not show agreement details until we know the status.
    const statusResult = await this.fetchAndApplyStatusByOffer(offerId, { silent: false });

    if (!this._isMounted) return;

    if (!statusResult || !statusResult.ok) {
      this.setState({
        initialStatusLoading: false,
        initialStatusError: statusResult ? statusResult.error : 'Failed to fetch status',
      });
      return;
    }

    this.setState({
      initialStatusLoading: false,
      initialStatusError: null,
    });

    // If already signed, we can skip template fetch entirely (fast path).
    if (statusResult.status === 'completed') {
      this.setState({ template: {} }); // minimal truthy value to render signed UI
      this.stopPolling();
      return;
    }

    // 2) Not signed yet: fetch template mapping + parameters.
    this.setState({ loading: true, error: null });

    try {
      const templateId = await this.fetchTemplateIdFromOffer(offerId);
      if (!templateId) throw new Error('No HelloSign template assigned to this offer.');

      await this.fetchTemplateParameters(templateId);
    } catch (e) {
      this.setState({
        error: (e && e.message) || 'Failed to load HelloSign template',
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchTemplateIdFromOffer = async (offerId) => {
    const url = `${RESTAPI_URI}/hellosign/offer-template/${offerId}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: localStorage.getItem('authToken'),
      },
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        `Failed to load offer-template mapping (${res.status}).\nURL: ${url}\n${text || ''}`
      );
    }

    const json = await res.json().catch(() => null);

    const item =
      json && (json.item || json.data || json.result)
        ? json.item || json.data || json.result
        : null;

    const templateId =
      (item && (item.templateId || item.template_id || item.hellosignTemplateId)) ||
      (json && (json.templateId || json.template_id)) ||
      null;

    if (!templateId) {
      throw new Error(`Offer mapping did not include templateId.\nURL: ${url}`);
    }

    this.setState({ templateId: String(templateId) });
    return String(templateId);
  };

  fetchTemplateParameters = async (templateId) => {
    const url = `${RESTAPI_URI}/hellosign/template/${templateId}/parameters`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: localStorage.getItem('authToken'),
      },
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        `Failed to load template parameters (${res.status}).\nURL: ${url}\n${text || ''}`
      );
    }

    const json = await res.json().catch(() => null);

    if (!json || !json.ok || !json.item) {
      throw new Error(`Invalid template parameters response.\nURL: ${url}`);
    }

    this.setState({ template: json.item });
  };

  /* ------------------------------ Payload Build ---------------------------- */

  getPrimarySignerFromTemplate = () => {
    const { template } = this.state;
    if (!template || !Array.isArray(template.signers) || !template.signers.length) return null;

    const s = template.signers[0] || null;
    const fields = (s && s.fields) || {};
    const nameObj = fields.name || null;
    const emailObj = fields.email || null;

    const name = nameObj && nameObj.value ? String(nameObj.value) : null;
    const email = emailObj && emailObj.value ? String(emailObj.value) : null;
    const role = s && s.role ? String(s.role) : 'SIGNER_1';

    return { role, name, email };
  };

  buildSendPayloadFromTemplate = () => {
    const { offerId, templateId, template } = this.state;

    const primary = this.getPrimarySignerFromTemplate();
    if (!primary || !primary.email || !primary.name) {
      throw new Error('Signer name/email not available from template parameters.');
    }

    const signers = [
      {
        role: primary.role,
        name: primary.name,
        email: primary.email,
      },
    ];

    const customFields = Array.isArray(template && template.customFields)
      ? template.customFields
        .filter((f) => f && f.key)
        .map((f) => ({
          name: String(f.key),
          value: f && typeof f.value !== 'undefined' && f.value !== null ? String(f.value) : '',
        }))
      : [];
    
    return {
      offerId,
      templateId,
      title: template && template.title ? template.title : 'Signature Request',
      subject: template && template.title ? template.title : 'Signature Request',
      message: 'Hi! Please review and sign.',
      testMode: RUNTIME_ENV == 'PROD' ? false : true,
      signers,
      customFields,
      metadata: { offerId },
    };
  };

  /* ------------------------------ Actions ---------------------------------- */

  sendOrRemind = async () => {
    this.setState({ actionLoading: true, actionError: null, actionMessage: null });

    try {
      const payload = this.buildSendPayloadFromTemplate();

      const sendUrl = `${RESTAPI_URI}/hellosign/signature-request/template`;
      const sendRes = await fetch(sendUrl, {
        method: 'POST',
        headers: {
          Authorization: localStorage.getItem('authToken'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const sendJson = await sendRes.json().catch(() => null);

      if (sendRes.ok && sendJson && sendJson.ok) {
        this.setState({
          signatureStatus: 'pending',
          signatureRequestId: sendJson.signatureRequestId || null,
          detailsUrl:
            (sendJson.helloSign &&
              sendJson.helloSign.signature_request &&
              sendJson.helloSign.signature_request.details_url)
              ? sendJson.helloSign.signature_request.details_url
              : null,
          actionMessage: 'Signature request email sent.',
          actionLoading: false,
        });

        this.syncStatusToForm('pending');
        this.startPolling('pending');
        return;
      }

      if (sendJson && sendJson.code === 'SIGNATURE_REQUEST_ALREADY_PENDING') {
        const signatureRequestId =
          sendJson.signatureRequestId || (sendJson.item && sendJson.item.signatureRequestId) || null;

        const signer = this.getPrimarySignerFromTemplate();

        if (!signatureRequestId) {
          throw new Error('Pending signature request exists but signatureRequestId was not returned.');
        }
        if (!signer || !signer.email) {
          throw new Error('Cannot remind signer because signer email is missing.');
        }

        const remindUrl = `${RESTAPI_URI}/hellosign/signature-request/remind`;
        const remindRes = await fetch(remindUrl, {
          method: 'POST',
          headers: {
            Authorization: localStorage.getItem('authToken'),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            signatureRequestId,
            email: signer.email,
            name: signer.name || undefined,
          }),
        });

        const remindJson = await remindRes.json().catch(() => null);

        if (!remindRes.ok) {
          const msg = await this.getErrorMessage(
            remindRes,
            remindJson,
            `Failed to resend signing email (${remindRes.status})`
          );
          throw new Error(msg);
        }

        this.setState({
          signatureStatus: 'pending',
          signatureRequestId,
          detailsUrl: (sendJson.item && sendJson.item.detailsUrl) ? sendJson.item.detailsUrl : null,
          actionMessage: 'Signing email resent.',
          actionLoading: false,
        });

        this.syncStatusToForm('pending');
        this.startPolling('pending');
        return;
      }

      if (sendJson && sendJson.code === 'SIGNATURE_REQUEST_ALREADY_COMPLETED') {
        this.setState({
          signatureStatus: 'completed',
          signatureRequestId:
            sendJson.signatureRequestId || (sendJson.item && sendJson.item.signatureRequestId) || null,
          detailsUrl: null,
          actionMessage: 'This document is already signed.',
          actionLoading: false,
        });

        this.syncStatusToForm('completed');
        this.stopPolling();
        return;
      }

      const errMsg = await this.getErrorMessage(
        sendRes,
        sendJson,
        `Failed to send signature request (${sendRes.status})`
      );
      throw new Error(errMsg);
    } catch (e) {
      const msg =
        e && typeof e === 'object'
          ? (e.message || (e.error_msg ? String(e.error_msg) : null) || JSON.stringify(e))
          : String(e || 'Failed to send/remind signature request');

      this.setState({
        actionError: msg,
        actionLoading: false,
      });
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                             Formal UI Sections                             */
  /* -------------------------------------------------------------------------- */

  renderStatusHeader() {
    const { classes } = this.props;
    const {
      initialStatusLoading,
      initialStatusError,
      checkingStatus,
      statusError,
      signatureStatus,
    } = this.state;

    const showSpinner = initialStatusLoading || checkingStatus;
    const effectiveError = initialStatusError || statusError;

    return (
      <div className={classes.card} style={{ marginTop: 10 }}>
        <Typography className={classes.helperText}>
          {initialStatusLoading
            ? 'We are verifying the signature status before loading any agreement details.'
            : signatureStatus === 'completed'
              ? 'The agreement has been signed and is ready for submission.'
              : 'The agreement must be signed before you can continue.'}
        </Typography>

        {showSpinner ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <CircularProgress size={18} />
            <Typography className={classes.helperText}>
              Checking signature status…
            </Typography>
          </div>
        ) : null}

        {effectiveError ? (
          <div style={{ marginTop: 12 }}>
            <div className={classes.divider} />
            <Typography className={classes.errorText}>{effectiveError}</Typography>
          </div>
        ) : null}
      </div>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                                  Sections                                  */
  /* -------------------------------------------------------------------------- */

  renderSignerFields() {
    const { template, signatureStatus, showDetails } = this.state;
    const { classes } = this.props;

    if (!showDetails) return null;
    if (signatureStatus === 'completed') return null;
    if (!template || !Array.isArray(template.signers) || !template.signers.length) return null;

    const isNarrow = typeof window !== 'undefined' ? window.innerWidth < 760 : false;
    const gridClass = isNarrow ? classes.twoColSingle : classes.twoCol;

    return (
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Signers</Typography>

        <div className={gridClass}>
          {template.signers.map((s, idx) => {
            const role = s && s.role ? String(s.role) : `SIGNER_${idx + 1}`;
            const fields = (s && s.fields) || {};

            const nameObj = fields.name || null;
            const emailObj = fields.email || null;

            const nameKey = (nameObj && nameObj.key) ? String(nameObj.key) : `${role}_NAME`;
            const emailKey = (emailObj && emailObj.key) ? String(emailObj.key) : `${role}_EMAIL`;

            const nameValue = nameObj ? nameObj.value : '';
            const emailValue = emailObj ? emailObj.value : '';

            return (
              <div key={`${role}_${idx}`} className={classes.card}>
                <div className={classes.cardHeaderRow}>
                  <span className={classes.rolePill}>{role}</span>
                </div>

                <div className={classes.fieldWrap}>
                  <Field
                    name={`hellosign_signer_${idx}_${nameKey}`}
                    component={renderTextInput}
                    label={`${role} name`}
                    type="text"
                    prefillValue={nameValue}
                    disabled
                  />
                </div>

                <div className={classes.fieldWrap}>
                  <Field
                    name={`hellosign_signer_${idx}_${emailKey}`}
                    component={renderTextInput}
                    label={`${role} email`}
                    type="text"
                    prefillValue={emailValue}
                    disabled
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderCustomFields() {
    const { template, signatureStatus, showDetails } = this.state;
    const { classes } = this.props;

    if (!showDetails) return null;
    if (signatureStatus === 'completed') return null;

    if (!template || !Array.isArray(template.customFields) || !template.customFields.length) {
      return (
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Custom Fields</Typography>
          <Typography className={classes.helperText}>No custom fields found.</Typography>
        </div>
      );
    }

    return (
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Custom Fields</Typography>

        <div className={classes.card}>
          {template.customFields.map((field) => {
            const key = field && field.key ? String(field.key) : '';
            if (!key) return null;

            const type = field && field.type === 'date' ? 'date' : 'text';
            const value = field ? field.value : '';

            return (
              <div key={key} className={classes.fieldWrap}>
                <Field
                  name={`hellosign_${key}`}
                  component={renderTextInput}
                  label={key}
                  type={type}
                  prefillValue={value}
                  disabled
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderBottomActions() {
    const { classes } = this.props;
    const {
      actionLoading,
      actionError,
      actionMessage,
      signatureStatus,
      checkingStatus,
      showDetails,
      initialStatusLoading,
      initialStatusError,
    } = this.state;

    let buttonLabel = 'Send signature request';
    let disabled = !!actionLoading || !!checkingStatus || !!initialStatusLoading;

    if (signatureStatus === 'pending') buttonLabel = 'Resend signing email';
    if (signatureStatus === 'completed') {
      buttonLabel = 'Already signed';
      disabled = true;
    }

    if (initialStatusError) {
      disabled = disabled || false;
    }

    const canToggleDetails = signatureStatus !== 'completed';

    return (
      <div className={classes.bottomBar}>
        <div className={classes.bottomLeft}>
          <span className={classes.statusPill}>
            <span className={classes.statusDot} style={this.getStatusDotStyle()} />
            {initialStatusLoading ? 'Checking…' : this.getStatusLabel()}
          </span>

          {initialStatusLoading ? (
            <span className={classes.helperText}>Verifying status…</span>
          ) : checkingStatus ? (
            <span className={classes.helperText}>Checking status…</span>
          ) : null}

          {actionLoading ? <CircularProgress size={18} /> : null}
        </div>

        <div className={classes.bottomRight}>
          {canToggleDetails ? (
            <Button
              className={classes.secondaryButton}
              variant="raised"
              onClick={this.toggleDetails}
              disabled={actionLoading || checkingStatus || initialStatusLoading}
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </Button>
          ) : null}

          <Button
            variant="raised"
            disabled={disabled}
            onClick={this.sendOrRemind}
            className={classes.primaryActionButton}
          >
            {buttonLabel}
          </Button>
        </div>

        {actionError ? (
          <div style={{ width: '100%' }}>
            <div className={classes.divider} />
            <Typography className={classes.errorText}>{actionError}</Typography>
          </div>
        ) : null}

        {actionMessage ? (
          <div style={{ width: '100%' }}>
            <div className={classes.divider} />
            <Typography className={classes.successText}>{actionMessage}</Typography>
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    const {
      loading,
      error,
      template,
      signatureStatus,
      detailsUrl,
      showDetails,
      initialStatusLoading,
      initialStatusError,
    } = this.state;

    const canRenderAgreementBody = !initialStatusLoading && !initialStatusError;

    return (
      <Grid container justify="center" className={classes.container}>
        <Grid item xs={12} sm={10} md={8}>
          <Typography align="center" className={classes.pageTitle}>
            Subscription Agreement
          </Typography>

          {this.renderStatusHeader()}

          {initialStatusError ? (
            <div className={classes.card}>
              <Typography className={classes.errorText}>
                We could not verify the agreement status at this time. Please refresh and try again.
                If the issue persists, contact support and include your offer ID.
              </Typography>
            </div>
          ) : null}

          {canRenderAgreementBody && loading ? (
            <div className={classes.card}>
              <Typography className={classes.helperText}>
                Loading agreement details…
              </Typography>
            </div>
          ) : null}

          {canRenderAgreementBody && error ? (
            <div className={classes.card}>
              <Typography className={classes.errorText}>{error}</Typography>
            </div>
          ) : null}

          {canRenderAgreementBody && template ? (
            <div>
              {signatureStatus !== 'completed' ? (
                <div className={classes.warningCard}>
                  <div className={classes.warningTitle}>
                    Signature required to continue
                  </div>

                  {/* UPDATED COPY (no fragments) */}
                  <div className={classes.warningText}>
                    When you click “Send signature request”, an email will be sent to your registered email address from Hellosign.
                    <br /><br />
                    This email will contain your subscription agreement and instructions to review and electronically sign the document. The signing process is secure and typically takes only a few minutes to complete.
                    <br /><br />
                    If you do not see the email shortly, please check your spam or junk folder.
                  </div>
                </div>
              ) : null}

              {signatureStatus !== 'completed' ? (
                <div>
                  {this.renderSignerFields()}
                  {this.renderCustomFields()}
                </div>
              ) : (
                <div className={classes.card} style={{ marginTop: 10 }}>
                  <Typography className={classes.sectionTitle}>Document</Typography>
                  <Typography className={classes.successText}>
                    This document has already been signed. No further action is required.
                  </Typography>
                </div>
              )}

              {this.renderBottomActions()}
            </div>
          ) : null}
        </Grid>
      </Grid>
    );
  }
}

/* -------------------------------------------------------------------------- */
/*                                   Props                                    */
/* -------------------------------------------------------------------------- */

HellosignAgreement.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.object,
  }),
  change: PropTypes.func,
};

HellosignAgreement.defaultProps = {
  match: null,
  change: null,
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(withStyles(styles)(HellosignAgreement));



// WEBPACK FOOTER //
// ./src/components/OfferOverview/ReadyToInvestButton/PaymentProcess/PaymentProcessForm/HellosignAgreement.js